import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subscription, catchError, finalize, of } from 'rxjs';
import {
  DatePickerComponent,
  PageContextGuardComponent,
} from '@clinic-platform/ui-components';

import { AuthService } from '../../../core/auth/auth.service';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { ProcedureTypesService } from '../../procedure-types/data-access/procedure-types.service';
import { ProcedureTypeItem } from '../../procedure-types/data-access/procedure-types.models';
import { SlotsService } from '../../slots/data-access/slots.service';
import {
  SlotCapacityMatrixAxisDto,
  SlotCapacityMatrixCellDto,
  SlotCapacityMatrixState,
} from '../../slots/data-access/slots.models';

interface CapacityMatrixBucket {
  id: string;
  label: string;
  start: string;
  end: string;
}

@Component({
  selector: 'app-appointment-capacity-matrix-page',
  standalone: true,
  imports: [
    FormsModule,
    NgClass,
    PageHeaderComponent,
    LoadingBlockComponent,
    EmptyStateComponent,
    CompanyContextRequiredComponent,
    PageContextGuardComponent,
    DatePickerComponent,
  ],
  template: `
    <app-page-header
      title="Capacity Matrix"
      subtitle="Resource-pair overview by hour"
    />

    <lib-page-context-guard [blocked]="companyContextMissing">
      <app-company-context-required />
    </lib-page-context-guard>

    @if (!companyContextMissing) {
      <section class="capacity-matrix">
        <div class="capacity-matrix__toolbar">
          <lib-date-picker
            label="Date"
            inputId="capacity-matrix-date"
            [value]="selectedDate"
            (valueChange)="onDateChanged($event)"
          />

          <label class="capacity-matrix__field">
            <span>Clinic</span>
            <select
              [ngModel]="selectedClinicId"
              (ngModelChange)="onClinicChanged($event)"
            >
              <option value="">Select clinic</option>
              @for (clinic of clinicOptions; track clinic.id) {
                <option [value]="clinic.id">{{ clinic.label }}</option>
              }
            </select>
          </label>

          <label class="capacity-matrix__field">
            <span>Procedure</span>
            <select
              [ngModel]="selectedProcedureTypeId"
              (ngModelChange)="onProcedureChanged($event)"
            >
              <option value="">All procedures</option>
              @for (procedure of procedureOptions; track procedure.id) {
                <option [value]="procedure.id">{{ procedure.name }}</option>
              }
            </select>
          </label>

          <button
            type="button"
            class="capacity-matrix__refresh"
            [disabled]="isLoading || !selectedClinicId"
            (click)="loadCapacityMatrix()"
          >
            Refresh
          </button>
        </div>

        @if (isLoading) {
          <app-loading-block text="Loading capacity matrix..." />
        } @else if (errorText) {
          <app-empty-state
            title="Failed to load capacity matrix"
            [description]="errorText"
          />
        } @else if (!selectedClinicId) {
          <app-empty-state
            title="Clinic is required"
            description="Select a clinic to load capacity matrix."
          />
        } @else if (devices.length === 0 || specialists.length === 0 || buckets.length === 0) {
          <app-empty-state
            title="No matrix data"
            description="No resource-pair buckets were returned for the selected scope."
          />
        } @else {
          <div class="capacity-matrix__panel">
            <div class="capacity-matrix__bucket-strip" aria-label="Hour buckets">
              @for (bucket of buckets; track bucket.id) {
                <button
                  type="button"
                  class="capacity-matrix__bucket"
                  [class.capacity-matrix__bucket--active]="bucket.id === selectedBucketId"
                  (click)="selectBucket(bucket.id)"
                >
                  {{ bucket.label }}
                </button>
              }
            </div>

            <div class="capacity-matrix__legend" aria-label="Cell states">
              @for (state of stateLegend; track state) {
                <span class="capacity-matrix__legend-item">
                  <span class="capacity-matrix__legend-swatch" [ngClass]="stateClass(state)"></span>
                  {{ stateLabel(state) }}
                </span>
              }
            </div>

            <div class="capacity-matrix__grid-wrap">
              <table class="capacity-matrix__grid">
                <thead>
                  <tr>
                    <th class="capacity-matrix__corner">Specialist</th>
                    @for (device of devices; track device.id) {
                      <th class="capacity-matrix__device">{{ device.label }}</th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (specialist of specialists; track specialist.id) {
                    <tr>
                      <th class="capacity-matrix__specialist">{{ specialist.label }}</th>
                      @for (device of devices; track device.id) {
                        <td>
                          @if (cellFor(device.id, specialist.id); as cell) {
                            <button
                              type="button"
                              class="capacity-matrix__cell"
                              [ngClass]="cellClass(cell)"
                              [class.capacity-matrix__cell--selected]="selectedCell?.id === cell.id"
                              (click)="selectCell(cell)"
                            >
                              <span class="capacity-matrix__cell-score">{{ percent(cell.score) }}</span>
                              <span class="capacity-matrix__cell-meta">
                                {{ cell.appointmentCount }} appt
                              </span>
                            </button>
                          } @else {
                            <span class="capacity-matrix__cell capacity-matrix__cell--empty">-</span>
                          }
                        </td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </div>

            @if (selectedCell) {
              <aside class="capacity-matrix__detail">
                <div>
                  <strong>{{ stateLabel(selectedCell.state) }}</strong>
                  <span>{{ selectedBucketLabel }}</span>
                </div>
                <dl>
                  <div>
                    <dt>Score</dt>
                    <dd>{{ percent(selectedCell.score) }}</dd>
                  </div>
                  <div>
                    <dt>Appointments</dt>
                    <dd>{{ selectedCell.appointmentCount }}</dd>
                  </div>
                  <div>
                    <dt>Device idle</dt>
                    <dd>{{ selectedCell.idleDeviceMinutes }} min</dd>
                  </div>
                  <div>
                    <dt>Specialist idle</dt>
                    <dd>{{ selectedCell.idleSpecialistMinutes }} min</dd>
                  </div>
                  <div>
                    <dt>Blocked</dt>
                    <dd>{{ selectedCell.blockedMinutes }} min</dd>
                  </div>
                  <div>
                    <dt>Reason</dt>
                    <dd>{{ selectedCell.reasonCode }}</dd>
                  </div>
                </dl>
              </aside>
            }
          </div>
        }
      </section>
    }
  `,
  styles: [`
    :host {
      display: block;
      width: 100%;
      min-width: 0;
    }

    .capacity-matrix {
      display: grid;
      gap: 16px;
      min-width: 0;
    }

    .capacity-matrix__toolbar {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      align-items: end;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #ffffff;
      min-width: 0;
    }

    .capacity-matrix__field {
      display: grid;
      gap: 6px;
      min-width: min(240px, 100%);
      flex: 1 1 240px;
      color: #374151;
      font-size: 13px;
    }

    .capacity-matrix__field select {
      min-height: 40px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 0 12px;
      background: #ffffff;
      color: #111827;
    }

    .capacity-matrix__refresh {
      min-height: 40px;
      border: 1px solid #2563eb;
      border-radius: 8px;
      padding: 0 14px;
      background: #2563eb;
      color: #ffffff;
      font-weight: 600;
      cursor: pointer;
    }

    .capacity-matrix__refresh:disabled {
      cursor: not-allowed;
      border-color: #cbd5e1;
      background: #e5e7eb;
      color: #64748b;
    }

    .capacity-matrix__panel {
      display: grid;
      gap: 12px;
      min-width: 0;
      padding: 12px;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      background: #ffffff;
    }

    .capacity-matrix__bucket-strip {
      display: flex;
      gap: 6px;
      min-width: 0;
      overflow-x: auto;
      padding-bottom: 2px;
    }

    .capacity-matrix__bucket {
      flex: 0 0 auto;
      min-height: 32px;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      padding: 0 10px;
      background: #ffffff;
      color: #334155;
      cursor: pointer;
    }

    .capacity-matrix__bucket--active {
      border-color: #1d4ed8;
      background: #eff6ff;
      color: #1d4ed8;
      font-weight: 700;
    }

    .capacity-matrix__legend {
      display: flex;
      flex-wrap: wrap;
      gap: 8px 14px;
      align-items: center;
      color: #475569;
      font-size: 12px;
    }

    .capacity-matrix__legend-item {
      display: inline-flex;
      gap: 6px;
      align-items: center;
    }

    .capacity-matrix__legend-swatch {
      width: 12px;
      height: 12px;
      border-radius: 3px;
      border: 1px solid rgba(15, 23, 42, 0.18);
    }

    .capacity-matrix__grid-wrap {
      width: 100%;
      max-width: 100%;
      overflow: auto;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
    }

    .capacity-matrix__grid {
      width: max-content;
      min-width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      table-layout: fixed;
    }

    .capacity-matrix__grid th,
    .capacity-matrix__grid td {
      border-right: 1px solid #e2e8f0;
      border-bottom: 1px solid #e2e8f0;
      padding: 6px;
      background: #ffffff;
      vertical-align: middle;
    }

    .capacity-matrix__grid th:last-child,
    .capacity-matrix__grid td:last-child {
      border-right: 0;
    }

    .capacity-matrix__grid tbody tr:last-child th,
    .capacity-matrix__grid tbody tr:last-child td {
      border-bottom: 0;
    }

    .capacity-matrix__corner,
    .capacity-matrix__specialist {
      position: sticky;
      left: 0;
      z-index: 2;
      width: 190px;
      max-width: 190px;
      color: #0f172a;
      font-size: 12px;
      text-align: left;
    }

    .capacity-matrix__corner {
      top: 0;
      z-index: 4;
      background: #f8fafc;
    }

    .capacity-matrix__specialist {
      background: #ffffff;
      font-weight: 700;
    }

    .capacity-matrix__device {
      position: sticky;
      top: 0;
      z-index: 3;
      width: 124px;
      max-width: 124px;
      background: #f8fafc;
      color: #0f172a;
      font-size: 12px;
      text-align: center;
    }

    .capacity-matrix__cell {
      display: grid;
      place-items: center;
      gap: 2px;
      width: 112px;
      min-height: 58px;
      border: 1px solid rgba(15, 23, 42, 0.12);
      border-radius: 7px;
      padding: 6px;
      color: #0f172a;
      cursor: pointer;
      transition: border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease;
    }

    .capacity-matrix__cell:hover {
      border-color: rgba(37, 99, 235, 0.55);
      box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.12);
    }

    .capacity-matrix__cell--selected {
      border-color: #1d4ed8;
      box-shadow: 0 0 0 2px rgba(29, 78, 216, 0.2);
    }

    .capacity-matrix__cell--empty {
      cursor: default;
      color: #94a3b8;
      background: #f8fafc;
    }

    .capacity-matrix__cell-score {
      font-weight: 800;
      font-size: 15px;
    }

    .capacity-matrix__cell-meta {
      color: rgba(15, 23, 42, 0.68);
      font-size: 11px;
    }

    .capacity-matrix__state--healthy {
      background: #e0f2fe;
    }

    .capacity-matrix__state--constrained {
      background: #fef3c7;
    }

    .capacity-matrix__state--blocked {
      background: #fee2e2;
    }

    .capacity-matrix__state--idle {
      background: #f1f5f9;
    }

    .capacity-matrix__state--mismatch {
      background: #fce7f3;
    }

    .capacity-matrix__state--opportunity {
      background: #dcfce7;
    }

    .capacity-matrix__detail {
      display: grid;
      gap: 10px;
      padding: 12px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      background: #f8fafc;
      color: #0f172a;
    }

    .capacity-matrix__detail > div {
      display: flex;
      justify-content: space-between;
      gap: 12px;
      align-items: center;
    }

    .capacity-matrix__detail span {
      color: #64748b;
      font-size: 13px;
    }

    .capacity-matrix__detail dl {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      gap: 8px;
      margin: 0;
    }

    .capacity-matrix__detail dl div {
      display: grid;
      gap: 2px;
      padding: 8px;
      border-radius: 7px;
      background: #ffffff;
    }

    .capacity-matrix__detail dt {
      color: #64748b;
      font-size: 11px;
      text-transform: uppercase;
    }

    .capacity-matrix__detail dd {
      margin: 0;
      font-weight: 700;
    }

    @media (max-width: 760px) {
      .capacity-matrix__toolbar {
        display: grid;
      }

      .capacity-matrix__field {
        min-width: 0;
        width: 100%;
      }

      .capacity-matrix__refresh {
        justify-self: start;
      }

      .capacity-matrix__panel {
        padding: 8px 6px;
      }

      .capacity-matrix__corner,
      .capacity-matrix__specialist {
        width: 150px;
        max-width: 150px;
      }
    }
  `],
})
export class AppointmentCapacityMatrixPageComponent implements OnInit, OnDestroy {
  private readonly authService = inject(AuthService);
  private readonly settingsService = inject(SettingsService);
  private readonly procedureTypesService = inject(ProcedureTypesService);
  private readonly slotsService = inject(SlotsService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly subscriptions = new Subscription();

  protected readonly stateLegend: SlotCapacityMatrixState[] = [
    'healthy',
    'constrained',
    'blocked',
    'idle',
    'mismatch',
    'opportunity',
  ];
  protected clinicOptions: Array<{ id: string; label: string; timeZone: string }> = [];
  protected procedureOptions: ProcedureTypeItem[] = [];
  protected selectedClinicId = '';
  protected selectedProcedureTypeId = '';
  protected selectedDate = this.todayDateKey();
  protected devices: SlotCapacityMatrixAxisDto[] = [];
  protected specialists: SlotCapacityMatrixAxisDto[] = [];
  protected buckets: CapacityMatrixBucket[] = [];
  protected selectedBucketId = '';
  protected selectedCell: SlotCapacityMatrixCellDto | null = null;
  protected isLoading = false;
  protected errorText = '';
  protected companyContextMissing = false;

  private readonly cellsByBucket = new Map<string, SlotCapacityMatrixCellDto[]>();
  private readonly isSystemAdmin = this.authService.getCurrentRole() === 'system_admin';

  public ngOnInit(): void {
    this.selectedClinicId = this.systemAdminContext.getClinicId()?.trim() ?? '';
    this.refreshCompanyContextState();
    this.loadClinicOptions();

    if (this.isSystemAdmin) {
      this.subscriptions.add(this.systemAdminContext.companyId$.subscribe(() => {
        this.selectedClinicId = this.systemAdminContext.getClinicId()?.trim() ?? '';
        this.refreshCompanyContextState();
        this.loadClinicOptions();
      }));
      this.subscriptions.add(this.systemAdminContext.clinicId$.subscribe((clinicId) => {
        this.selectedClinicId = clinicId?.trim() ?? '';
        this.loadProcedureOptions();
        this.loadCapacityMatrix();
      }));
      this.subscriptions.add(this.systemAdminContext.scopeMode$.subscribe(() => {
        this.refreshCompanyContextState();
      }));
    }
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  protected onDateChanged(value: string): void {
    this.selectedDate = value;
    this.loadCapacityMatrix();
  }

  protected onClinicChanged(value: string): void {
    this.selectedClinicId = value?.trim() ?? '';
    this.selectedProcedureTypeId = '';
    this.loadProcedureOptions();
    this.loadCapacityMatrix();
  }

  protected onProcedureChanged(value: string): void {
    this.selectedProcedureTypeId = value?.trim() ?? '';
    this.loadCapacityMatrix();
  }

  protected loadCapacityMatrix(): void {
    this.errorText = '';
    this.resetMatrix();

    if (this.companyContextMissing || !this.selectedClinicId) {
      return;
    }

    this.isLoading = true;
    this.slotsService.getCapacityMatrix({
      clinicId: this.selectedClinicId,
      schedulingDate: this.selectedDate,
      procedureTypeId: this.selectedProcedureTypeId || undefined,
    }).pipe(
      catchError((error: unknown) => {
        console.error('Failed to load capacity matrix', error);
        this.errorText = 'Capacity matrix read model is unavailable for the selected scope.';
        return of({
          devices: [],
          specialists: [],
          cells: [],
        });
      }),
      finalize(() => {
        this.isLoading = false;
      }),
    ).subscribe((response) => {
      this.devices = response.devices ?? [];
      this.specialists = response.specialists ?? [];
      this.buildBuckets(response.cells ?? []);
    });
  }

  protected selectBucket(bucketId: string): void {
    this.selectedBucketId = bucketId;
    this.selectedCell = null;
  }

  protected selectCell(cell: SlotCapacityMatrixCellDto): void {
    this.selectedCell = cell;
  }

  protected cellFor(deviceId: string, specialistId: string): SlotCapacityMatrixCellDto | null {
    return (this.cellsByBucket.get(this.selectedBucketId) ?? []).find((cell) =>
      cell.deviceId === deviceId && cell.specialistUserId === specialistId,
    ) ?? null;
  }

  protected cellClass(cell: SlotCapacityMatrixCellDto): string {
    return this.stateClass(cell.state);
  }

  protected stateClass(state: SlotCapacityMatrixState): string {
    return `capacity-matrix__state--${state}`;
  }

  protected stateLabel(state: SlotCapacityMatrixState): string {
    switch (state) {
      case 'opportunity':
        return 'Opportunity';
      case 'constrained':
        return 'Constrained';
      case 'blocked':
        return 'Blocked';
      case 'idle':
        return 'Idle';
      case 'mismatch':
        return 'Mismatch';
      case 'healthy':
      default:
        return 'Healthy';
    }
  }

  protected percent(value: number): string {
    return `${Math.round(Math.max(0, Math.min(1, value)) * 100)}%`;
  }

  protected get selectedBucketLabel(): string {
    return this.buckets.find((bucket) => bucket.id === this.selectedBucketId)?.label ?? '';
  }

  private loadClinicOptions(): void {
    this.clinicOptions = [];
    this.procedureOptions = [];

    if (this.companyContextMissing) {
      return;
    }

    this.settingsService.getClinicOperationalSettings().subscribe({
      next: (items: ClinicOperationalSettingsDto[]) => {
        this.clinicOptions = items.map((item) => ({
          id: item.clinicId,
          label: item.clinicName,
          timeZone: item.timeZone || item.timeZoneInherited || 'UTC',
        }));

        if (!this.selectedClinicId && this.clinicOptions.length === 1) {
          this.selectedClinicId = this.clinicOptions[0].id;
        }

        this.loadProcedureOptions();
        this.loadCapacityMatrix();
      },
      error: (error: unknown) => {
        console.error('Failed to load clinics for capacity matrix', error);
        this.errorText = 'Clinic options are unavailable for the current scope.';
      },
    });
  }

  private loadProcedureOptions(): void {
    this.procedureOptions = [];

    if (!this.selectedClinicId || this.companyContextMissing) {
      return;
    }

    this.procedureTypesService.getList({
      includeInactive: false,
      clinicId: this.selectedClinicId,
    }).subscribe({
      next: (items) => {
        this.procedureOptions = items.filter((item) => item.isActive);
      },
      error: (error: unknown) => {
        console.error('Failed to load procedure options for capacity matrix', error);
      },
    });
  }

  private buildBuckets(cells: SlotCapacityMatrixCellDto[]): void {
    this.cellsByBucket.clear();

    const bucketById = new Map<string, CapacityMatrixBucket>();

    for (const cell of cells) {
      const id = cell.bucketStart;
      const list = this.cellsByBucket.get(id) ?? [];
      list.push(cell);
      this.cellsByBucket.set(id, list);

      if (!bucketById.has(id)) {
        bucketById.set(id, {
          id,
          label: this.bucketLabel(cell.bucketStart, cell.bucketEnd, cell.clinicTimeZone),
          start: cell.bucketStart,
          end: cell.bucketEnd,
        });
      }
    }

    this.buckets = Array.from(bucketById.values())
      .sort((left, right) => new Date(left.start).getTime() - new Date(right.start).getTime());
    this.selectedBucketId = this.buckets[0]?.id ?? '';
  }

  private bucketLabel(start: string, end: string, timeZone: string): string {
    return `${this.timeLabel(start, timeZone)}-${this.timeLabel(end, timeZone)}`;
  }

  private timeLabel(value: string, timeZone: string): string {
    return new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      timeZone,
    }).format(new Date(value));
  }

  private resetMatrix(): void {
    this.devices = [];
    this.specialists = [];
    this.buckets = [];
    this.selectedBucketId = '';
    this.selectedCell = null;
    this.cellsByBucket.clear();
  }

  private refreshCompanyContextState(): void {
    this.companyContextMissing =
      this.isSystemAdmin
      && (
        this.systemAdminContext.isGlobalScope()
        || !this.systemAdminContext.getCompanyId()?.trim()
      );
  }

  private todayDateKey(): string {
    return new Date().toISOString().slice(0, 10);
  }
}
